import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import EntityReferenceComponent from "../../../../components/entitiesReferences/EntityReferenceComponent";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import { RichTextEditor } from "../../../../components/field/richTextEditor/RichTextEditor";
import usePersonConnectionQuery from "../../../../hooks/query/connections/usePersonConnectionQuery";
import { Person } from "../../../../types/person";
import {
  PERSON_CONNECTION_TYPE_LABELS,
  PersonConnection,
  SystemPersonConnectionTypes,
} from "../../../../types/personConnection";

interface PersonConnectionsViewSectionProps {
  person: Person;
}

const formatConnectionType = (
  type: SystemPersonConnectionTypes | null
): string =>
  (type && PERSON_CONNECTION_TYPE_LABELS[type]) || "N/A";

const getConnectionFields = (
  connection: PersonConnection
): ViewAccordionField[] => [
  {
    label: "Connection Type",
    value: (
      <Typography variant="body1">
        {formatConnectionType(connection?.system_person_connection_type ?? null)}
      </Typography>
    ),
  },
  {
    label: "Notes",
    value: (
      <RichTextEditor
        value={typeof connection?.notes === "string" ? connection.notes : ""}
        readOnly
        fullWidth
        placeholder="N/A"
      />
    ),
  },
];

const PersonConnectionsViewSection: React.FC<
  PersonConnectionsViewSectionProps
> = ({ person }) => {
  const { useFetchConnectedEdges } = usePersonConnectionQuery();
  const { data, isLoading, errorMessage } = useFetchConnectedEdges(
    person?._id,
    "outbound",
    {}
  );

  const items = useMemo<PersonConnection[]>(
    () => data?.data ?? [],
    [data]
  );

  return (
    <ConnectionAccordionSection<PersonConnection>
      title="Person Connections"
      loading={isLoading}
      error={errorMessage}
      items={items}
      getItemId={(connection, index) =>
        connection?._key ?? `person-connection-${index}`
      }
      getItemTitle={(connection) =>
        connection?.toDoc ? (
          <EntityReferenceComponent entity={connection.toDoc} linkMode="view" />
        ) : (
          <Typography variant="body1">N/A</Typography>
        )
      }
      getItemSubtitle={(connection) =>
        formatConnectionType(connection?.system_person_connection_type ?? null)
      }
      getItemFields={(connection) => getConnectionFields(connection)}
      emptyText="No person connections"
    />
  );
};

export default PersonConnectionsViewSection;
